import { useMutation } from "@apollo/client";
import { compact } from "lodash";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import {
  AssessmentDeliveryState,
  AssessmentGroupDelivery,
  AssessmentQuestionType,
  AssessmentState,
  ComplianceProgramCalibrationStatus,
  ComplianceProgramCalibrationType,
  ComplianceProgramPerformanceReportRowFragment,
  GetComplianceProgramForReportQuery,
  SaveAssessmentDeliveryMutation,
  SaveAssessmentDeliveryMutationVariables,
  UnsubmitAssessmentMutation,
  UnsubmitAssessmentMutationVariables,
  UpdateCalibrationStatusMutation,
  UpdateCalibrationStatusMutationVariables,
} from "types/graphql-schema";

import CalibrationChangelogModal from "@apps/assessments/components/calibration/calibration-changelog-modal";
import reopenAssessmentMutation from "@apps/assessments/graphql/reopen-assessment-mutation";
import updateAssessmentDeliveryMutation from "@apps/assessments/graphql/update-assessment-delivery-mutation";
import getComplianceProgramForReportQuery from "@apps/reporting/graphql/get-compliance-program-for-report-query";
import updateCalibrationStatusMutation from "@apps/reporting/graphql/update-calibration-status-mutation";
import useLabel from "@apps/use-label/use-label";
import {
  currentUserVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import Dropdown, {
  DropdownOptionType,
  removeConsecutiveDividers,
} from "@components/dropdown/dropdown";
import StatusPill, {
  StatusPillEnum,
} from "@components/status-pill/status-pill";
import { TableBodyCell, TableBodyRow } from "@components/table/table";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import EmployeeDrawerSidebar from "./employee-drawer/employee-drawer-sidebar";
import { PerformanceAssessmentTableQuestion } from "./performance-assessment-table";
import PerformanceAssessmentTableEmptyCell from "./performance-assessment-table-empty-cell";
import PerformanceAssessmentTableNameCell from "./performance-assessment-table-name-cell";
import PerformanceAssessmentTableRowAnswerCell from "./performance-assessment-table-row-answer-cell";
import PerformanceAssessmentTablePrecalibrationCell from "./performance-assessment-table-row-precalibration-cell";
import { AssessmentReportTableColumn } from "./report-filters/types";
import usePrecalibrationData from "./use-precalibration-data";

const PerformanceAssessmentTableRow = ({
  reportRow,
  complianceProgram,
  questions,
  assessmentTemplate,
  visibleTableColumns,
  onRowActionConfirm,
  recentAnswerColumns = [],
}: {
  complianceProgram: NonNullable<
    GetComplianceProgramForReportQuery["complianceProgram"]
  >;
  reportRow: ComplianceProgramPerformanceReportRowFragment;
  questions: PerformanceAssessmentTableQuestion[];
  assessmentTemplate: NonNullable<
    NonNullable<
      GetComplianceProgramForReportQuery["complianceProgram"]
    >["performanceAssessmentTemplate"]
  >;
  visibleTableColumns: AssessmentReportTableColumn[];
  recentAnswerColumns?: number[];
  onRowActionConfirm: (message: string) => Promise<boolean>;
}) => {
  const label = useLabel();
  const isAdmin = isAdminVar();
  const currentUser = currentUserVar();

  const [isEmployeeDrawerOpen, setIsEmployeeDrawerOpen] = useState(false);
  const [isChangelogModalOpen, setIsChangelogModalOpen] = useState(false);

  const handleCloseChangelogModal = useCallback(() => {
    setIsChangelogModalOpen(false);
  }, []);

  const responder = useMemo(
    () =>
      reportRow.assessment
        ? assertNonNull(reportRow.assessment.responder)
        : reportRow.user.primaryManager,
    [reportRow.assessment, reportRow.user]
  );
  const target = useMemo(() => assertNonNull(reportRow.user), [reportRow.user]);
  const answers = useMemo(
    () =>
      reportRow.assessment ? compact([...reportRow.assessment.answers]) : [],
    [reportRow.assessment]
  );
  const answerIdByQuestionId = useMemo(() => {
    return answers.reduce(
      (acc, ans) => ({
        ...acc,
        [ans.questionId]: ans.id,
      }),
      {} as Record<number, number>
    );
  }, [answers]);

  const { precalibrationData } = usePrecalibrationData({
    complianceProgramId: complianceProgram.id,
    templateId: assessmentTemplate.id,
    skip:
      complianceProgram.calibration !==
      ComplianceProgramCalibrationType.PreCalibration,
  });
  const rowHasCalibrations = useMemo(
    () =>
      (precalibrationData &&
        precalibrationData.filter(
          (calibration) => calibration.target?.id === reportRow.user.id
        ).length > 0) ||
      answers.some(
        (answer) => answer.hasCalibrations || answer.hasCalibrationSuggestions
      ),
    [answers, precalibrationData, reportRow]
  );

  const prevAnswers = useMemo(
    () =>
      reportRow.assessment?.mostRecentAnswers
        ? compact(reportRow.assessment.mostRecentAnswers)
        : [],
    [reportRow.assessment]
  );

  const { hasPreCalibration, hasPostCalibration } = useMemo(
    () => ({
      hasPreCalibration:
        complianceProgram.calibration ===
        ComplianceProgramCalibrationType.PreCalibration,
      hasPostCalibration:
        complianceProgram.calibration ===
        ComplianceProgramCalibrationType.PostCalibration,
    }),
    [complianceProgram.calibration]
  );
  const assessmentDeliveryState = useMemo(() => {
    if (reportRow.assessment) {
      if (reportRow.assessment.assessmentDelivery) {
        return reportRow.assessment.assessmentDelivery.state;
      }
      return reportRow.assessment.assessmentDeliveryState;
    }
    return null;
  }, [reportRow.assessment]);
  const canCalibrateAnswers = useMemo(
    () =>
      isAdmin &&
      ((hasPreCalibration &&
        (reportRow.calibrationStatus ===
          ComplianceProgramCalibrationStatus.NotStarted ||
          !!reportRow.assessment)) ||
        (hasPostCalibration && !!reportRow.assessment)),
    [
      hasPostCalibration,
      hasPreCalibration,
      isAdmin,
      reportRow.assessment,
      reportRow.calibrationStatus,
    ]
  );
  const canSuggestCalibration = useMemo(
    () =>
      canCalibrateAnswers ||
      (hasPreCalibration &&
        !reportRow.assessment &&
        reportRow.calibrationStatus ===
          ComplianceProgramCalibrationStatus.NotStarted) ||
      (hasPostCalibration &&
        !!reportRow.assessment &&
        reportRow.calibrationStatus ===
          ComplianceProgramCalibrationStatus.NotStarted),
    [
      canCalibrateAnswers,
      hasPreCalibration,
      reportRow.calibrationStatus,
      reportRow.assessment,
      hasPostCalibration,
    ]
  );
  const canUpdateDeliveryPackage = useMemo(
    () =>
      reportRow.assessment?.assessmentDelivery &&
      !!reportRow.assessment.assessmentDelivery.canUpdate?.permission,
    [reportRow.assessment]
  );
  const canReadAssessmentDelivery = useMemo(
    () =>
      reportRow.assessment?.assessmentDelivery &&
      !!reportRow.assessment.assessmentDelivery.canRead?.permission,
    [reportRow.assessment]
  );
  const isTargetManager = useMemo(
    () => target.primaryManager?.id === currentUser.id,
    [target, currentUser]
  );
  const [saveAssessmentDelivery] = useMutation<
    SaveAssessmentDeliveryMutation,
    SaveAssessmentDeliveryMutationVariables
  >(updateAssessmentDeliveryMutation);
  const [updateCalibrationStatus] = useMutation<
    UpdateCalibrationStatusMutation,
    UpdateCalibrationStatusMutationVariables
  >(updateCalibrationStatusMutation);

  const [reopenAssessment] = useMutation<
    UnsubmitAssessmentMutation,
    UnsubmitAssessmentMutationVariables
  >(reopenAssessmentMutation);

  const isAdminApprovalDelivery = useMemo(
    () => assessmentTemplate.delivery === AssessmentGroupDelivery.AdminApproval,
    [assessmentTemplate]
  );
  const isManagerApprovalDelivery = useMemo(
    () => assessmentTemplate.delivery === AssessmentGroupDelivery.Partial,
    [assessmentTemplate]
  );

  const confirmationMap = useMemo(
    () => ({
      [AssessmentDeliveryState.AwaitingAdminApproval]: `Proceeding will mark this delivery package as Not Started. If the package has been delivered already, ${target.name} will no longer have access to it. Do you want to continue?`,
      [AssessmentDeliveryState.Draft]: `Proceeding will Approve this delivery package and allow the manager of ${target.name} to share it whenever they choose. No further responses will be gathered. Do you want to continue?`,
      [AssessmentDeliveryState.Delivered]: isTargetManager
        ? `Proceeding will share the content of the ${label("review")} with ${
            target.name
          }. Do you want to continue?`
        : `This functionality is typically only meant to be used by the Manager of the Subject for this ${label(
            "review"
          )}. Proceeding will share the content of the ${label(
            "review"
          )} with ${target.name}. Do you want to continue?`,
    }),
    [isTargetManager, label, target]
  );
  const handleChangeDeliveryState = useCallback(
    async (
      state: AssessmentDeliveryState,
      opts: {
        successMsg: string;
        confirmMsg: string;
      }
    ) => {
      const confirmation = await onRowActionConfirm(opts.confirmMsg);
      if (confirmation && reportRow.assessment?.assessmentDelivery) {
        saveAssessmentDelivery({
          variables: {
            assessmentDeliveryId: reportRow.assessment.assessmentDelivery.id,
            state,
          },
          onCompleted: () => {
            successNotificationVar({
              title: opts.successMsg,
            });
          },
        });
      }
    },
    [onRowActionConfirm, reportRow.assessment, saveAssessmentDelivery]
  );
  const handleClickNotStarted = useCallback(async () => {
    handleChangeDeliveryState(AssessmentDeliveryState.AwaitingAdminApproval, {
      successMsg: `${label("review", {
        capitalize: true,
      })} Package marked as Not Started`,
      confirmMsg:
        confirmationMap[AssessmentDeliveryState.AwaitingAdminApproval],
    });
  }, [confirmationMap, handleChangeDeliveryState, label]);
  const handleClickApprove = useCallback(async () => {
    handleChangeDeliveryState(AssessmentDeliveryState.Draft, {
      successMsg: `${label("review", {
        capitalize: true,
      })} Package Approved for Delivery`,
      confirmMsg: confirmationMap[AssessmentDeliveryState.Draft],
    });
  }, [confirmationMap, handleChangeDeliveryState, label]);
  const handleClickUnCalibrate = useCallback(async () => {
    updateCalibrationStatus({
      variables: {
        complianceProgramId: complianceProgram.id,
        targetId: target.id,
        status: ComplianceProgramCalibrationStatus.NotStarted,
      },
      onCompleted: () => {
        successNotificationVar({
          title: `${label("review", {
            capitalize: true,
          })} Package marked as Not Calibrated`,
        });
      },
    });
  }, [complianceProgram, label, target, updateCalibrationStatus]);
  const handleClickCalibrate = useCallback(async () => {
    const msg =
      complianceProgram.calibration ===
      ComplianceProgramCalibrationType.PreCalibration
        ? `This will make the downward ${label(
            "review"
          )} available to the manager. You will not be able to edit any ratings again until the after the manager submits the downward ${label(
            "review"
          )}. Are you sure you want to continue?`
        : "Proceeding will mark this delivery package as Calibrated. Managers will still not be able to share it until it is marked as Approved. Do you want to continue?";
    const confirmation = await onRowActionConfirm(msg);
    if (confirmation) {
      updateCalibrationStatus({
        variables: {
          complianceProgramId: complianceProgram.id,
          targetId: target.id,
          status: ComplianceProgramCalibrationStatus.Calibrated,
        },
        onCompleted: () => {
          successNotificationVar({
            title:
              complianceProgram.calibration ===
              ComplianceProgramCalibrationType.PreCalibration
                ? `${target.name} marked as Calibrated`
                : `${label("review", {
                    capitalize: true,
                  })} Package marked as Calibrated`,
          });
        },
      });
    }
  }, [
    complianceProgram,
    label,
    onRowActionConfirm,
    target,
    updateCalibrationStatus,
  ]);
  const handleClickDeliver = useCallback(async () => {
    handleChangeDeliveryState(AssessmentDeliveryState.Delivered, {
      successMsg: `${label("review", {
        capitalize: true,
      })} Package Delivered`,
      confirmMsg: confirmationMap[AssessmentDeliveryState.Delivered],
    });
  }, [confirmationMap, handleChangeDeliveryState, label]);
  const handleClickUndoDeliver = useCallback(async () => {
    handleChangeDeliveryState(
      isAdminApprovalDelivery
        ? AssessmentDeliveryState.AwaitingAdminApproval
        : AssessmentDeliveryState.Draft,
      {
        successMsg: `${label("review", {
          capitalize: true,
        })} Package delivery undone`,
        confirmMsg: `Proceeding will mark this delivery package as Not Started. ${target.name} will no longer have access to it. Do you want to continue?`,
      }
    );
  }, [handleChangeDeliveryState, label, target.name, isAdminApprovalDelivery]);

  const handleClickSendBackSubmissionToManager = useCallback(async () => {
    const confirmation = await onRowActionConfirm(
      `Reopening the ${label(
        "review"
      )} will allow the responder to edit their responses. Do you want to continue?`
    );
    if (confirmation && reportRow.assessment) {
      reopenAssessment({
        variables: {
          assessmentId: reportRow.assessment.id,
        },
        onCompleted: () => {
          successNotificationVar({
            title: `${label("review", {
              capitalize: true,
            })} reopened. The responder can now edit their responses.`,
          });
        },
        refetchQueries: [getComplianceProgramForReportQuery],
      });
    }
  }, [onRowActionConfirm, reportRow.assessment, reopenAssessment, label]);

  const handleClickOpenChangelogModal = useCallback(() => {
    setIsChangelogModalOpen(true);
  }, []);

  const handleClickOpenEmployeeDrawer = useCallback(() => {
    setIsEmployeeDrawerOpen(true);
  }, []);

  const handleCloseEmployeeDrawer = useCallback(() => {
    setIsEmployeeDrawerOpen(false);
  }, []);

  const actionDropdownOptions: DropdownOptionType[] = useMemo(() => {
    if (
      hasPreCalibration &&
      isAdmin &&
      reportRow.calibrationStatus ===
        ComplianceProgramCalibrationStatus.NotStarted
    ) {
      return [
        {
          label: "Mark as Calibrated",
          onClick: handleClickCalibrate,
          type: "button",
        },
      ];
    }
    return removeConsecutiveDividers(
      compact([
        (isAdminApprovalDelivery || hasPostCalibration) &&
          (canUpdateDeliveryPackage || canCalibrateAnswers) &&
          isAdmin && {
            label: "Change Status to...",
            type: "submenu",
            options: compact([
              canCalibrateAnswers &&
                reportRow.calibrationStatus ===
                  ComplianceProgramCalibrationStatus.Calibrated && {
                  label: "Not Calibrated",
                  onClick: handleClickUnCalibrate,
                  type: "button",
                },
              canCalibrateAnswers &&
                reportRow.calibrationStatus ===
                  ComplianceProgramCalibrationStatus.NotStarted && {
                  label: "Calibrated",
                  onClick: handleClickCalibrate,
                  type: "button",
                },
              canUpdateDeliveryPackage &&
                assessmentDeliveryState !==
                  AssessmentDeliveryState.AwaitingAdminApproval && {
                  label: "Not Approved for Sharing",
                  onClick: handleClickNotStarted,
                  type: "button",
                },
              canUpdateDeliveryPackage &&
                assessmentDeliveryState !== AssessmentDeliveryState.Draft && {
                  label: "Approved for Sharing",
                  onClick: handleClickApprove,
                  type: "button",
                },
            ]),
          },
        (isAdminApprovalDelivery || isManagerApprovalDelivery) &&
          canUpdateDeliveryPackage &&
          isAdmin &&
          assessmentDeliveryState !== AssessmentDeliveryState.Delivered && {
            label: `Deliver this ${label("review", { capitalize: true })}`,
            onClick: handleClickDeliver,
            type: "button",
          },
        (isAdminApprovalDelivery || isManagerApprovalDelivery) &&
          canUpdateDeliveryPackage &&
          isAdmin &&
          assessmentDeliveryState === AssessmentDeliveryState.Delivered && {
            label: `Undo Delivery of this ${label("review", {
              capitalize: true,
            })} Package`,
            onClick: handleClickUndoDeliver,
            type: "button",
          },
        (isAdminApprovalDelivery || isManagerApprovalDelivery) &&
          canUpdateDeliveryPackage &&
          isAdmin &&
          assessmentDeliveryState !== AssessmentDeliveryState.Delivered && {
            label: `Send Back to Manager`,
            onClick: handleClickSendBackSubmissionToManager,
            type: "button",
          },
        (isAdminApprovalDelivery || isManagerApprovalDelivery) && {
          type: "divider",
        },
        (hasPreCalibration || hasPostCalibration) &&
          reportRow.assessment && {
            label: `View Change Log`,
            onClick: handleClickOpenChangelogModal,
            type: "button",
          },
        { type: "divider" },
        reportRow.assessment?.assessmentDelivery &&
          canReadAssessmentDelivery && {
            label: `View ${label("review", { capitalize: true })} Package`,
            onClick: handleClickOpenEmployeeDrawer,
            type: "button",
          },
      ])
    );
  }, [
    isAdminApprovalDelivery,
    canCalibrateAnswers,
    canUpdateDeliveryPackage,
    isAdmin,
    reportRow.calibrationStatus,
    reportRow.assessment,
    handleClickUnCalibrate,
    handleClickCalibrate,
    assessmentDeliveryState,
    handleClickNotStarted,
    handleClickApprove,
    isManagerApprovalDelivery,
    label,
    handleClickDeliver,
    handleClickUndoDeliver,
    handleClickSendBackSubmissionToManager,
    hasPostCalibration,
    hasPreCalibration,
    handleClickOpenChangelogModal,
    canReadAssessmentDelivery,
    handleClickOpenEmployeeDrawer,
  ]);

  const columnContent = useMemo(() => {
    return visibleTableColumns.map((column) => {
      switch (column.id) {
        case "name":
          return (
            <PerformanceAssessmentTableNameCell
              key={column.id}
              isEmployeeDrawerOpen={isEmployeeDrawerOpen}
              onOpenEmployeeDrawer={handleClickOpenEmployeeDrawer}
              target={target}
              actionDropdown={
                actionDropdownOptions.length > 0 && (
                  <Dropdown
                    align="end"
                    options={actionDropdownOptions}
                    aria-label="Performance assessment report row dropdown"
                  />
                )
              }
              viewCalibrations={
                (hasPreCalibration || hasPostCalibration) &&
                rowHasCalibrations && (
                  <Button
                    className="shrink-0"
                    icon={TopicflowIconMap.userEdit}
                    theme={buttonTheme.iconGray}
                    tooltip="View the calibration comments and edits"
                    onClick={handleClickOpenChangelogModal}
                  />
                )
              }
            />
          );
        case "status":
          return isAdminApprovalDelivery ? (
            <TableBodyCell key={column.id}>
              <div className="min-w-48 flex flex-nowrap grow shrink-0 items-center justify-between gap-2">
                <Tooltip
                  text={
                    assessmentDeliveryState === AssessmentDeliveryState.Draft
                      ? `The ${label(
                          "review"
                        )} was approved by an admin. The manager can now share it with the subject.`
                      : assessmentDeliveryState ===
                        AssessmentDeliveryState.AwaitingAdminApproval
                      ? `The ${label(
                          "review"
                        )} is awaiting approval by an admin.`
                      : assessmentDeliveryState ===
                        AssessmentDeliveryState.Delivered
                      ? `The ${label("review")} was shared with the subject.`
                      : reportRow.assessment?.state ===
                        AssessmentState.Submitted
                      ? `The ${label("review")} was submitted by the manager.`
                      : !!assessmentDeliveryState
                      ? `The ${label("review")} is in draft state.`
                      : `The ${label("review")} hasn't been submitted yet.`
                  }
                >
                  <div>
                    <StatusPill
                      label={
                        assessmentDeliveryState ===
                        AssessmentDeliveryState.Draft
                          ? "Approved"
                          : assessmentDeliveryState ===
                            AssessmentDeliveryState.Delivered
                          ? "Shared"
                          : "Not Approved"
                      }
                      status={
                        assessmentDeliveryState ===
                        AssessmentDeliveryState.Draft
                          ? StatusPillEnum.InProgress
                          : assessmentDeliveryState ===
                            AssessmentDeliveryState.Delivered
                          ? StatusPillEnum.Complete
                          : StatusPillEnum.NotApplicable
                      }
                    />
                  </div>
                </Tooltip>
              </div>
            </TableBodyCell>
          ) : (
            <TableBodyCell key={column.id}>
              <div className="min-w-48 flex flex-nowrap grow shrink-0 items-center justify-between gap-2">
                <Tooltip
                  text={
                    assessmentDeliveryState === AssessmentDeliveryState.Draft
                      ? `The manager can now share the ${label(
                          "review"
                        )} with the subject.`
                      : assessmentDeliveryState ===
                        AssessmentDeliveryState.Delivered
                      ? `The ${label("review")} was shared with the subject.`
                      : `The ${label("review")} hasn't been submitted yet.`
                  }
                >
                  <div>
                    <StatusPill
                      label={
                        assessmentDeliveryState ===
                        AssessmentDeliveryState.Draft
                          ? "Not Shared"
                          : assessmentDeliveryState ===
                            AssessmentDeliveryState.Delivered
                          ? "Shared"
                          : "N/A"
                      }
                      status={
                        assessmentDeliveryState ===
                        AssessmentDeliveryState.Delivered
                          ? StatusPillEnum.Complete
                          : StatusPillEnum.NotApplicable
                      }
                    />
                  </div>
                </Tooltip>
              </div>
            </TableBodyCell>
          );
        case "calibration-status":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-32 flex">
                <StatusPill
                  label={
                    reportRow.calibrationStatus ===
                    ComplianceProgramCalibrationStatus.NotStarted
                      ? "Not Calibrated"
                      : "Calibrated"
                  }
                  status={
                    reportRow.calibrationStatus !==
                    ComplianceProgramCalibrationStatus.NotStarted
                      ? StatusPillEnum.Complete
                      : StatusPillEnum.NotApplicable
                  }
                />
              </div>
            </TableBodyCell>
          );
        case "manager-status":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-32 flex">
                <StatusPill
                  label={reportRow.assessment ? "Completed" : "In Progress"}
                  status={
                    reportRow.assessment
                      ? StatusPillEnum.Complete
                      : StatusPillEnum.InProgress
                  }
                />
              </div>
            </TableBodyCell>
          );
        case "enrollment-date":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">
                {moment(reportRow.targetAddedToProgramOn).format("MMM D, YYYY")}
              </div>
            </TableBodyCell>
          );
        case "current-role-track":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">
                {reportRow.roles
                  .map((role) => `${role.careerTrack.title}`)
                  .join(", ")}
              </div>
            </TableBodyCell>
          );
        case "current-role-level":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">
                {reportRow.roles.map((role) => `${role.level}`).join(", ")}
              </div>
            </TableBodyCell>
          );
        case "current-role":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">
                {reportRow.roles.map((role) => `${role.title}`).join(", ")}
              </div>
            </TableBodyCell>
          );
        case "manager":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">{responder?.name}</div>
            </TableBodyCell>
          );
        case "position":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">{reportRow.position}</div>
            </TableBodyCell>
          );
        // eslint-disable-next-line local-rules/use-label
        case "team":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">
                {target.teamsList.map(({ title }) => title).join(", ")}
              </div>
            </TableBodyCell>
          );
        case "role-avg":
          return (
            <TableBodyCell key={column.id}>
              <div
                className={classNames(
                  "min-w-36",
                  !reportRow.assessment?.roleAssessmentAvg && "text-gray-300"
                )}
              >
                {reportRow.assessment
                  ? reportRow.assessment.roleAssessmentAvg ?? "N/A"
                  : null}
              </div>
            </TableBodyCell>
          );
        case "self-role-avg":
          return (
            <TableBodyCell key={column.id}>
              <div
                className={classNames(
                  "min-w-36",
                  !reportRow.assessment?.selfRoleAssessmentAvg &&
                    "text-gray-300"
                )}
              >
                {reportRow.assessment
                  ? reportRow.assessment.selfRoleAssessmentAvg ?? "N/A"
                  : null}
              </div>
            </TableBodyCell>
          );
        default: {
          const question = questions.find((q) => q.id === column.id);
          const isCalibratableQuestionType =
            !!question &&
            (question.questionType === AssessmentQuestionType.Range ||
              question.questionType === AssessmentQuestionType.TalentIndicator);
          const showPrevAnswer = question
            ? recentAnswerColumns.includes(question.id)
            : false;
          if (question && reportRow.assessment) {
            return (
              <PerformanceAssessmentTableRowAnswerCell
                key={question.id}
                complianceProgram={complianceProgram}
                assessmentTemplate={assessmentTemplate}
                assessment={reportRow.assessment}
                question={question}
                answers={answers}
                prevAnswers={prevAnswers}
                programHasCalibration={hasPostCalibration || hasPreCalibration}
                canCalibrate={canCalibrateAnswers && isCalibratableQuestionType}
                canSuggestCalibration={
                  canSuggestCalibration && isCalibratableQuestionType
                }
                showPrevAnswer={showPrevAnswer}
              />
            );
          } else if (question && !reportRow.assessment && hasPreCalibration) {
            return (
              <PerformanceAssessmentTablePrecalibrationCell
                key={question.id}
                complianceProgram={complianceProgram}
                assessmentTemplate={assessmentTemplate}
                target={reportRow.user}
                question={question}
                canCalibrate={canCalibrateAnswers && isCalibratableQuestionType}
                canSuggestCalibration={
                  canSuggestCalibration && isCalibratableQuestionType
                }
              />
            );
          }
          return (
            <PerformanceAssessmentTableEmptyCell
              key={column.id}
              padLeft={showPrevAnswer}
            />
          );
        }
      }
    });
  }, [
    actionDropdownOptions,
    answers,
    prevAnswers,
    reportRow,
    assessmentDeliveryState,
    canCalibrateAnswers,
    canSuggestCalibration,
    questions,
    responder,
    isAdminApprovalDelivery,
    hasPostCalibration,
    hasPreCalibration,
    target,
    visibleTableColumns,
    recentAnswerColumns,
    handleClickOpenEmployeeDrawer,
    handleClickOpenChangelogModal,
    isEmployeeDrawerOpen,
    label,
    rowHasCalibrations,
    complianceProgram,
    assessmentTemplate,
  ]);

  return (
    <>
      {isChangelogModalOpen && (
        <CalibrationChangelogModal
          reviewExists={!!reportRow.assessment}
          answerIdByQuestionId={answerIdByQuestionId}
          complianceProgramId={complianceProgram.id}
          templateId={assessmentTemplate.id}
          canCalibrate={canCalibrateAnswers}
          canSuggestCalibration={canSuggestCalibration}
          target={target}
          onOpenEmployeeDrawer={handleClickOpenEmployeeDrawer}
          onClose={handleCloseChangelogModal}
        />
      )}
      {isEmployeeDrawerOpen && (
        <EmployeeDrawerSidebar
          complianceProgram={complianceProgram}
          target={target}
          onClose={handleCloseEmployeeDrawer}
        />
      )}
      <TableBodyRow
        // needs 1px height for hoverable cells to take up the entire row height, not sure why
        className="h-[1px]"
        key={reportRow.id}
      >
        {columnContent}
      </TableBodyRow>
    </>
  );
};

export default PerformanceAssessmentTableRow;
